import type { Table } from "dexie";
import { DateTime } from "luxon";

import { BaseDexieRepository } from "./base-repository";
import type { AudioRecordingMetadataRecord } from "@/domains/dexie-db/models/audio-recording";

export type ListRecordingOptions = {
  limit?: number;
  order?: "asc" | "desc";
};

export class AudioRecordingMetadataRepository extends BaseDexieRepository<AudioRecordingMetadataRecord> {
  constructor(table: Table<AudioRecordingMetadataRecord, number>) {
    super(table);
  }

  async upsert(run: AudioRecordingMetadataRecord): Promise<void> {
    await this.transaction("rw", async () => {
      await this.table.where({ recordingId: run.recordingId }).delete();
      await this.table.add(run);
    });
  }

  async update(recordingId: string, patch: Partial<AudioRecordingMetadataRecord>): Promise<void> {
    await this.table.where({ recordingId }).modify(patch);
  }

  async getByRecordingId(recordingId: string): Promise<AudioRecordingMetadataRecord | undefined> {
    return this.table.where({ recordingId }).first();
  }

  async list({ limit, order = "desc" }: ListRecordingOptions = {}): Promise<
    AudioRecordingMetadataRecord[]
  > {
    let collection = this.table.orderBy("createdAt");
    if (order === "desc") {
      collection = collection.reverse();
    }
    if (typeof limit === "number") {
      collection = collection.limit(limit);
    }
    const results = await collection.toArray();
    return results.filter(run => run.softDeletedAt == null);
  }

  async deleteByRecordingId(recordingId: string): Promise<void> {
    await this.table.where({ recordingId }).delete();
  }

  async clearByNoteId(noteId: string): Promise<void> {
    await this.table.where({ noteId }).delete();
  }

  async softDeleteByRecordingId(
    recordingId: string,
    timestamp = DateTime.now().toMillis()
  ): Promise<void> {
    await this.table
      .where({ recordingId })
      .modify({ softDeletedAt: timestamp, lastUpdatedAt: timestamp });
  }

  async softDeleteByNoteId(noteId: string, timestamp = DateTime.now().toMillis()): Promise<void> {
    await this.table
      .where({ noteId })
      .modify({ softDeletedAt: timestamp, lastUpdatedAt: timestamp });
  }
}
